/**
 * patternDrillHelper
 *
 * Shared pattern detail-panel renderer + in-panel drill, extracted from
 * `InsightsSection.renderPatternDetailPanel` / `renderPatternInstances` /
 * `handleDiscussPattern` the moment the renderer gained a second consumer —
 * the same move `sessionDrillHelper.js` (session detail → practice/thread
 * pages) and `guideDrillHelper.js` (guide detail → `/search`) made.
 * `InsightsSection.js` (existing consumer, the `/library` Patterns tab, which
 * keeps its own `right-panel:open` path and its page-owned education
 * sub-panel) and `SessionsSection.js` (new consumer: session detail's
 * Observations "See pattern" action) both import this module — no duplicate
 * implementation.
 *
 * `openPatternInPanel` mirrors `openSessionInPanel`: when the right panel can
 * drill, the pattern detail opens INSIDE the current panel with the
 * auto-wired back arrow; otherwise it falls back to the `/library` deep link.
 *
 * The `contentType` is always the literal `'library-pattern'` — the exact
 * string `InsightsSection.js` already emits — so a panel opened from either
 * surface is recognized as closeable by whichever section's
 * `right-panel:closed` listener is currently active.
 *
 * @module components/patternDrillHelper
 */

import page from '../vendor/page.js';
import { escapeHtml, eventBus } from '../core/utils.js';
import { API } from '../core/api.js';
import { renderDisclosure, wireDisclosures, zoneWrapHtml } from './disclosure.js';
import { iconArrowRight, iconArrowUpRight } from '../utils/icons.js';
import { openSessionInPanel } from './sessionDrillHelper.js';

/** Right-panel `contentType` shared by both pattern-detail consumers. */
export const PATTERN_DETAIL_CONTENT_TYPE = 'library-pattern';

/**
 * Disclosure key for the pattern detail's observed-instances zone. Stable so
 * an open/closed choice can be remembered across the panel's async
 * re-render (`InsightsSection.loadPatternDetail`).
 */
export const PATTERN_INSTANCES_ZONE = 'pattern-instances';

/**
 * Module-level launch-in-flight guard for `launchPatternDiscussion`. The
 * eventBus dispatch is fire-and-forget (ModeOrchestrator's async handler runs
 * unawaited past the call), so without a guard a rapid double-click before
 * the first navigation lands would fire two launches — mirrors
 * `guideDrillHelper.js`'s `atomLaunchInFlight`.
 */
let patternDiscussionLaunchInFlight = false;

/**
 * The `/library` deep link for a pattern — the fallback when the panel cannot
 * drill, and the "Learn more" hand-off (see `openPatternInPanel`).
 * @param {string} patternCode - Pattern code
 * @returns {string} Path with query
 */
function libraryPatternPath(patternCode) {
  return `/library?tab=patterns&pattern=${encodeURIComponent(patternCode)}`;
}

/**
 * Format a date for instance display.
 * @param {string} dateStr - ISO date string
 * @returns {string} Formatted date (e.g., "Feb 5"), or '' when absent
 */
export function formatInstanceDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Capitalize the first letter of a string.
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string, or '' when absent
 */
function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Render the provenance qualifier for an observation.
 *
 * `confidence` records how the observation arose, not how certain we are it
 * is real: `high` means the user stated it outright, `medium`/`low` mean it
 * was implied or surfaced in conversation. Only the non-`high` case is
 * marked — a qualifier on every row would be a constant, and an unmarked
 * row already reads as "you said this". Unknown values stay unmarked.
 *
 * @param {string} confidence - Observation confidence ('high' | 'medium' | 'low')
 * @returns {string} HTML string, empty when the observation was stated outright
 */
export function renderObservationProvenance(confidence) {
  if (confidence !== 'medium' && confidence !== 'low') {
    return '';
  }

  return `<span class="tag timeline-row__qualifier" title="Implied, or surfaced in conversation — not stated outright">Implied</span>`;
}

/**
 * Render a pattern's observed instances: one timeline row per session, its
 * observations inside, closing on a "Session N ↗" tag (`data-action="view-session"`).
 * @param {Object} pattern - Pattern with instances data
 * @param {number} limit - Max instances to show
 * @returns {string} HTML string (a loading placeholder until `instances` exists)
 */
export function renderPatternInstances(pattern, limit) {
  // If we don't have full instances yet, show placeholder based on instanceCount
  if (!pattern.instances) {
    return `<p class="pattern-instances__placeholder">Loading instances...</p>`;
  }

  const instances = pattern.instances.slice(0, limit);

  if (instances.length === 0) {
    return '';
  }

  // Date-only left rail — the unified instance-row icon slot is for semantic
  // state, and the user's own avatar informs nothing on a single-user page.
  return instances.map(instance => `
      <div class="timeline-row pattern-instance" data-session-id="${escapeHtml(String(instance.sessionId))}">
        <div class="timeline-row__date-col pattern-instance__left">
          <span class="timeline-row__date pattern-instance__date">${formatInstanceDate(instance.sessionDate)}</span>
        </div>
        <div class="timeline-row__content pattern-instance__content">
          ${instance.observations.map(obs => `
            <div class="pattern-instance__observation">
              <div class="timeline-row__title-line">
                <p class="timeline-row__title pattern-instance__title">${escapeHtml(obs.title)}</p>
                ${renderObservationProvenance(obs.confidence)}
              </div>
              ${obs.evidence ? `<p class="timeline-row__detail pattern-instance__detail">${escapeHtml(obs.evidence)}</p>` : ''}
            </div>
          `).join('')}
          <span class="tag tag--session" data-action="view-session" data-session-number="${escapeHtml(String(instance.sessionNumber))}">
            Session ${escapeHtml(String(instance.sessionNumber))} ${iconArrowUpRight()}
          </span>
        </div>
      </div>
    `).join('');
}

/**
 * Render the pattern-detail panel body: a loading spinner while `pattern` is
 * not yet loaded, else the title, meta row, description, "Learn more" link
 * (`data-action="learn-more"`) and the observed-instances disclosure zone
 * (`PATTERN_INSTANCES_ZONE`).
 * @param {Object|null} pattern - Pattern (list-level or fully loaded), or
 *   null while still loading
 * @param {Object} [opts]
 * @param {boolean} [opts.instancesExpanded=true] - Whether the instances
 *   zone renders open (the consumer remembers the reader's choice across
 *   re-renders)
 * @returns {string} HTML string
 */
export function renderPatternDetailBody(pattern, { instancesExpanded = true } = {}) {
  if (!pattern) {
    return `
        <div class="pattern-detail">
          <div class="pattern-detail__loading">
            <div class="loading-spinner"></div>
          </div>
        </div>
      `;
  }

  return `
      <div class="pattern-detail">
        <h1 class="sd-title">${escapeHtml(pattern.name)}</h1>

        <div class="sd-metadata">
          <span class="sd-metadata__text"><b>${pattern.instanceCount}</b> Instance${pattern.instanceCount !== 1 ? 's' : ''}</span>
          <span class="sd-metadata__text"><b>${capitalizeFirst(pattern.aggregateConfidence)}</b> Confidence</span>
          <span class="sd-metadata__text">${escapeHtml(pattern.category)}</span>
        </div>

        <div class="sd-section-header">Description</div>
        <p class="sd-summary">${escapeHtml(pattern.userDescription)}</p>

        <a href="#" class="inline-link" data-action="learn-more" data-pattern="${escapeHtml(pattern.patternCode)}">
          Learn more about ${escapeHtml(pattern.name)}
          ${iconArrowRight()}
        </a>

        ${zoneWrapHtml(
    renderDisclosure(
      PATTERN_INSTANCES_ZONE,
      'Observed instances',
      `<div class="pattern-card__instances">
            ${renderPatternInstances(pattern, pattern.instances?.length ?? pattern.instanceCount)}
          </div>`,
      { expanded: instancesExpanded, count: pattern.instanceCount }
    ),
    { id: `${PATTERN_INSTANCES_ZONE}-zone` }
  )}
      </div>
    `;
}

/**
 * Hand a pattern off to chat — navigates into an ADVICE `discuss_pattern`
 * session. Identity only crosses (itemId + display-only sourceItem); the
 * backend reloads the pattern definition and the user's own instances of it
 * server-side (`AdviceStrategy._buildPatternDiscussionContext`).
 *
 * Guarded by the module-level in-flight flag: a rapid second call within the
 * same event-loop turn is a no-op.
 *
 * @param {Object} pattern - Pattern object (`patternCode`, `name`, `userDescription`)
 */
export function launchPatternDiscussion(pattern) {
  if (patternDiscussionLaunchInFlight) return;
  if (!pattern?.patternCode) return;

  patternDiscussionLaunchInFlight = true;

  eventBus.emit('navigate', {
    page: 'chat',
    mode: 'ADVICE',
    artifactDiscussion: {
      discriminator: 'discuss_pattern',
      itemId: pattern.patternCode,
      sourceItem: {
        code: pattern.patternCode,
        name: pattern.name,
        userDescription: pattern.userDescription
      }
    }
  });

  // The eventBus dispatch above is synchronous fire-and-forget — clear on
  // the next tick, once dispatch has fully unwound, so a rapid re-entrant
  // click within the same event-loop turn can't fire a second launch.
  setTimeout(() => { patternDiscussionLaunchInFlight = false; }, 0);
}

/**
 * Build the `right-panel:drill` / `right-panel:open` payload for a fully-loaded
 * pattern: the rendered body with its disclosure wired, delegated clicks for
 * the instance "Session N" tags and the "Learn more" link, and the "Discuss
 * pattern" primary button.
 *
 * @param {Object} pattern - Fully-loaded pattern (`API.getPatternByCode` shape)
 * @param {Object} opts
 * @param {(patternCode: string) => void} opts.onLearnMore - "Learn more" handler
 * @param {(sessionNumber: string) => void} [opts.onViewSession] - Instance
 *   "Session N" tag handler; defaults to `openSessionInPanel`
 * @returns {Object} right-panel payload
 */
export function buildPatternDrillPayload(pattern, { onLearnMore, onViewSession = openSessionInPanel }) {
  const content = document.createElement('div');
  content.innerHTML = renderPatternDetailBody(pattern);
  wireDisclosures(content);

  content.addEventListener('click', (e) => {
    const target = e.target.closest('[data-action]');
    if (!target) return;
    e.preventDefault();
    if (target.dataset.action === 'learn-more') {
      onLearnMore(target.dataset.pattern);
    } else if (target.dataset.action === 'view-session') {
      onViewSession(target.dataset.sessionNumber);
    }
  });

  return {
    title: '',
    titleAlign: 'left',
    primaryButton: {
      label: 'Discuss pattern',
      onClick: () => launchPatternDiscussion(pattern)
    },
    content,
    contentType: PATTERN_DETAIL_CONTENT_TYPE
  };
}

/**
 * Open a pattern detail from an inline link inside the current right panel.
 * If the panel can drill, fetches the full pattern and drills into its detail
 * in the same panel (back arrow returns). On any failure — panel at its depth
 * cap or closed, fetch returning null or throwing — falls back to the
 * `/library` pattern deep link.
 *
 * @param {string} patternCode - Pattern code from the link's data attribute
 * @returns {Promise<void>}
 */
export async function openPatternInPanel(patternCode) {
  if (!patternCode) return;

  const rightPanelManager = window.app?.modules?.rightPanelManager;
  if (rightPanelManager?.canDrill()) {
    try {
      const pattern = await API.getPatternByCode(patternCode);
      if (pattern) {
        eventBus.emit('right-panel:drill', buildPatternDrillPayload(pattern, {
          // The education sub-panel stays page-owned by InsightsSection
          // (`handleLearnMore` renders it from the section's own state), so
          // from a drilled pattern "Learn more" navigates to the /library
          // pattern page rather than drilling a third layer here.
          onLearnMore: (code) => page(libraryPatternPath(code))
        }));
        return;
      }
    } catch (err) {
      console.error('[patternDrillHelper] Failed to load pattern for drill:', err);
    }
  }

  // Fallback: the /library pattern deep link (InsightsSection resolves
  // `?pattern=` on activation).
  page(libraryPatternPath(patternCode));
}
